import merge from 'lodash/merge'
import { COUNTRY } from '../../../utils/constants'
import { inputSlice } from '../store/slices/input.slice'
import {
  ANCHORAGE_OPTION,
  CORROSION_RESISTANCE_VALUE,
  FOUNDATION_TYPE,
  GEOMETRY_TYPE,
  HOLDOWN_SUBTYPE,
  HOLDOWN_FASTENERS,
  LUMBER_SPECIES,
  MATERIAL_TYPE,
  POST_BASE_FASTENERS_TYPE,
  POST_BASE_LUMBER_SPECIES,
  SETTINGS_INITIAL_STATE,
  anchorageOptions,
  HOLDOWN_TYPE,
  GEOMETRY_SUBTYPE,
} from './constants'
import {
  geometrySubTypeData,
  geometryTypeData,
  geometryTypeImagesData,
  holdownFastenersTypeData,
  loadTypeData,
  lumberSpeciesData,
  materialTypeData,
  wallThicknessData,
  minStemwallThicknessData,
  postWidthData,
  postThicknessData,
} from './json/staticData.json'
import find from 'lodash/find'

const checkOptions = (val, opts) => {
  return opts.find((item) => item.value === val) ? true : false
}

export const geometryTypeImages = (holdownSubType, materialType) => {
  const cloneGeometryTypeImagesData = [...geometryTypeImagesData]

  if (holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP) {
    // PFD-128 for 1B only
    cloneGeometryTypeImagesData[GEOMETRY_TYPE.STEMWALL] = [
      {
        url: 'https://embed.widencdn.net/download/ssttoolbox/1ireifjjai/C_SSTB_InstIll_Ill_Instld_Figure2StemwallPerspective_CY.svg',
        label: 'Anchor in a Stemwall Foundation',
      },
      {
        url: 'https://embed.widencdn.net/svg/ssttoolbox/3uojcxdkaf/STHD_endwall_in_callout.svg?u=oviqdm',
        label: 'Anchor in a Stemwall Foundation',
      },
    ]
  }

  if (materialType === MATERIAL_TYPE.CMU) {
    cloneGeometryTypeImagesData[GEOMETRY_TYPE.STEMWALL] = [
      {
        url: 'https://ssttoolbox.widen.net/content/pknmpvayns/svg/PFD_CMU_SSTB_1.svg',
        label: 'Anchor in a Stemwall Foundation',
      },
      {
        url: 'https://ssttoolbox.widen.net/content/pzxl5pf70y/svg/PFD_CMU_SSTB_2.svg',
        label: 'Anchor in a Stemwall Foundation',
      },
    ]
  }

  return cloneGeometryTypeImagesData
}

export const getGeometryTypeOptions = (key, key2) => {
  // key is from Material, key2 is Anchor Type
  if (key === MATERIAL_TYPE.CMU) {
    return {
      options: geometryTypeData.materialType.cmuOptions,
    }
  }
  // key2:  [Cast-in-Place_Anchors(0), Adhesive_Anchors(1), Mechanical_Anchors(2)]

  const optionDictionary = {
    [ANCHORAGE_OPTION.CAST_IN_PLACE]: geometryTypeData.anchorageType.castInPlaceOptions,
    [ANCHORAGE_OPTION.ADHESIVE]: geometryTypeData.anchorageType.adhesiveOptions,
    [ANCHORAGE_OPTION.MECHANICAL]: geometryTypeData.anchorageType.mechanicalOptions,
  }

  return {
    options: optionDictionary[key2],
    optionsB: geometryTypeData.holdownSubType.embeddedStrapOptions,
  }
}

export const getGeometrySubTypeOptions = (key, key2, key3) => {
  // key : foundationType
  // key2 : materialType
  // key3 : anchorageOption
  if (key2 === MATERIAL_TYPE.CMU) {
    return {
      options: geometrySubTypeData.materialType.cmuOptions,
    }
  }
  // following are 'concrete' foundation type
  const optionDictionary = {
    [GEOMETRY_TYPE.STEMWALL]: geometrySubTypeData.foundationType.stemwallOptions,
    [GEOMETRY_TYPE.TURNED_DOWN_SLAB]: geometrySubTypeData.foundationType.turnedDownSlabOptions,
    [GEOMETRY_TYPE.BRICK_LEDGE]: geometrySubTypeData.foundationType.brickLedgeOptions,
    [GEOMETRY_TYPE.GARAGE_SLAB]: geometrySubTypeData.foundationType.garageSlabOptions,
    [GEOMETRY_TYPE.GARAGE_STEMWALL]: geometrySubTypeData.foundationType.garageStemwallOptions,
  }

  // PFD-115  IF <Anchor Type> = Mechanical Anchors
  // Do Not Show <Application> = "Corner Anchor"
  // 2/15/2021 : revert PFD-115 change
  const optionMechanicalAnchor = {
    [GEOMETRY_TYPE.ELEVATED_SLAB]:
      geometrySubTypeData.anchorageType.mechanicalAnchors.elevatedSlabOptions,
    [GEOMETRY_TYPE.INTERIOR_THICKENED_FOOTING]:
      geometrySubTypeData.anchorageType.mechanicalAnchors.interiorThickenedFootingOptions,
    [GEOMETRY_TYPE.STEMWALL]: geometrySubTypeData.anchorageType.mechanicalAnchors.stemwallOptions,
    [GEOMETRY_TYPE.TURNED_DOWN_SLAB]:
      geometrySubTypeData.anchorageType.mechanicalAnchors.turnedDownSlabOptions,
    [GEOMETRY_TYPE.BRICK_LEDGE]:
      geometrySubTypeData.anchorageType.mechanicalAnchors.brickLedgeOptions,
  }

  // PFD-183
  // IF <Design Anchorage> = TRUE
  // AND <Embedded Anchor> = TRUE
  // AND <Anchor Type> = Adhesive Anchors
  // AND <Foundation Type> = Stemwall
  // ADD <Application>: "Corner Anchor" option to the dropdown

  const optionAdhesiveAnchor = {
    [GEOMETRY_TYPE.ELEVATED_SLAB]:
      geometrySubTypeData.anchorageType.adhesiveAnchors.elevatedSlabOptions,
    [GEOMETRY_TYPE.INTERIOR_THICKENED_FOOTING]:
      geometrySubTypeData.anchorageType.adhesiveAnchors.interiorThickenedFootingOptions,
    [GEOMETRY_TYPE.STEMWALL]: geometrySubTypeData.anchorageType.adhesiveAnchors.stemwallOptions,
    [GEOMETRY_TYPE.TURNED_DOWN_SLAB]:
      geometrySubTypeData.anchorageType.adhesiveAnchors.turnedDownSlabOptions,
    [GEOMETRY_TYPE.BRICK_LEDGE]:
      geometrySubTypeData.anchorageType.adhesiveAnchors.brickLedgeOptions,
  }

  const getOptionsByKey = () => {
    switch (
      key3 // key3 : anchorageOption
    ) {
      case ANCHORAGE_OPTION.ADHESIVE:
        return optionAdhesiveAnchor[key] // 1C  adhesive
      case ANCHORAGE_OPTION.MECHANICAL:
        return optionMechanicalAnchor[key] // 1E  mechanical
      default:
        return optionDictionary[key] // 1D
    }
  }

  return {
    options: getOptionsByKey(),
  }
}

export const getMinStemwallThicknessOptions = (holdownDesign, foundationType) => {
  if (holdownDesign) {
    return minStemwallThicknessData.holdown
  }

  switch (foundationType) {
    case FOUNDATION_TYPE.CONCRETE_2175:
      return minStemwallThicknessData.postbase.concrete2175PsiOptions
    case FOUNDATION_TYPE.CONCRETE_2500:
      return minStemwallThicknessData.postbase.concrete2500PsiOptions
    case FOUNDATION_TYPE.CONCRETE_2900:
      return minStemwallThicknessData.postbase.concrete2900PsiOptions
    case FOUNDATION_TYPE.WOOD:
      return minStemwallThicknessData.postbase.woodOptions
    default:
      return [] // or the other default value
  }
}

const validate1A = (source, errors) => {
  let err = []
  err = Object.entries(source).reduce((result, [key, val]) => {
    const log = () => result.push('invalid ' + key + ' value ' + val)

    if (
      key === 'holdownDesign' ||
      key === 'upliftLoadASD' ||
      key === 'lumberSpecies' ||
      key === 'wallThickness' ||
      key === 'holdownFastenersType'
    ) {
    } else if (key === 'foundationDesign') {
      // should be false
      val === true ? log() : null
    } else if (key === 'holdownSubType') {
      // should be '0'
      val === '1' ? log() : null
    } else {
      result.push('invalid name ' + key + ' for A type ')
    }
    return result
  }, [])

  return errors.concat(err)
}

const validate1B = (source, errors) => {
  const { geometryType, materialType, anchorageOption } = source

  let err = []
  err = Object.entries(source).reduce((result, [key, val]) => {
    const log = () => result.push('invalid ' + key + ' value ' + val)

    if (
      key === 'holdownDesign' ||
      key === 'foundationDesign' ||
      key === 'holdownSubType' ||
      key === 'upliftLoadASD' ||
      key === 'loadType'
    ) {
    } else if (
      key === 'holdownFastenersType' ||
      key === 'lumberSpecies' ||
      key === 'wallThickness' ||
      key === 'installOverSheathing'
    ) {
    } else if (key === 'raisedFloor') {
    } else if (key === 'materialType') {
      val === 'concrete' ? null : log()
    } else if (key === 'compressiveStrength') {
      val === '0' ? null : log()
    } else if (key === 'geometryType') {
      val === '2' ? null : log()
    } else if (key === 'geometrySubType') {
      if (!geometryType || !materialType) {
        log()
      } else {
        checkOptions(
          val,
          getGeometrySubTypeOptions(geometryType, materialType, anchorageOption).options,
        )
          ? null
          : log()
      }
    } else {
      result.push('invalid name ' + key + ' for B type ')
    }
    return result
  }, [])

  return errors.concat(err)
}

const validate1CDE = (source, errors) => {
  const { geometryType, materialType, anchorageOption } = source

  let err = []
  err = Object.entries(source).reduce((result, [key, val]) => {
    const log = () => result.push('invalid ' + key + ' value ' + val)

    if (key === 'foundationDesign' || key === 'upliftLoadASD' || key === 'loadType') {
    } else if (key === 'materialType') {
    } else if (key === 'compressiveStrength') {
    } else if (key === 'anchorageOption') {
    } else if (key === 'geometryType') {
      if (!materialType || !anchorageOption) {
        log()
      } else {
        checkOptions(val, getGeometryTypeOptions(materialType, anchorageOption).options)
          ? null
          : log()
      }
    } else if (key === 'geometrySubType') {
      if (!geometryType || !materialType || !anchorageOption) {
        log()
      } else {
        checkOptions(
          val,
          getGeometrySubTypeOptions(geometryType, materialType, anchorageOption).options,
        )
          ? null
          : log()
      }
    } else {
      result.push('invalid name ' + key + ' for CDE type ')
    }
    return result
  }, [])

  return errors.concat(err)
}

const validate1ACDE = (source, errors) => {
  const { geometryType, materialType, anchorageOption } = source

  let err = []
  err = Object.entries(source).reduce((result, [key, val]) => {
    const log = () => result.push('invalid ' + key + ' value ' + val)

    if (
      key === 'holdownDesign' ||
      key === 'foundationDesign' ||
      key === 'holdownSubType' ||
      key === 'upliftLoadASD' ||
      key === 'loadType'
    ) {
    } else if (
      key === 'holdownFastenersType' ||
      key === 'lumberSpecies' ||
      key === 'wallThickness'
    ) {
    } else if (key === 'materialType') {
    } else if (key === 'compressiveStrength') {
    } else if (key === 'anchorageOption') {
    } else if (key === 'geometryType') {
      if (!materialType || !anchorageOption) {
        log()
      } else {
        checkOptions(val, getGeometryTypeOptions(materialType, anchorageOption).options)
          ? null
          : log()
      }
    } else if (key === 'geometrySubType') {
      if (!geometryType || !materialType || !anchorageOption) {
        log()
      } else {
        checkOptions(
          val,
          getGeometrySubTypeOptions(geometryType, materialType, anchorageOption).options,
        )
          ? null
          : log()
      }
    } else {
      result.push('invalid name ' + key + ' for ACDE type ')
    }
    return result
  }, [])

  return errors.concat(err)
}

/*
/pfd?holdownDesign=Yes
/pfd?holdownDesign=Yes&holdownFastenersType=0&holdownFastenersModel=0&upliftLoadASD=1720&lumberSpecies=1&wallThickness=3.5

/pfd?holdownDesign=Yes&anchorageDesign=Yes&holdownSubType=1
/pfd?holdownDesign=Yes&anchorageDesign=Yes&holdownSubType=1&upliftLoadASD=1720&loadType=0&holdownFastenersType=0&holdownFastenersModel=0&lumberSpecies=1&wallThickness=3.5&installOverSheathing=Y&materialType=concrete&compressiveStrength=0&geometryType=2&geometrySubType=1

/pfd?anchorageDesign=Yes&materialType=concrete&anchorageOption=0
/pfd?anchorageDesign=Yes&materialType=concrete&anchorageOption=0&upliftLoadASD=1720&loadType=0&compressiveStrength=0&geometryType=2&geometrySubType=1

/pfd?holdownDesign=Yes&anchorageDesign=Yes&materialType=concrete&anchorageOption=0
/pfd?holdownDesign=Yes&anchorageDesign=Yes&holdownFastenersType=0&holdownFastenersModel=0&upliftLoadASD=1720&lumberSpecies=2&wallThickness=3.5&materialType=concrete&anchorageOption=0&compressiveStrength=0&geometryType=2&geometrySubType=1
*/

const initValue = inputSlice.getInitialState()

export function cleanParams(params) {
  const whitelist = Object.keys(initValue)
  return Object.keys(params).reduce((result, key) => {
    if (!whitelist.includes(key)) return result
    result[key] = params[key]
    return result
  }, {})
}

export const validateParams = (source) => {
  const { holdownFastenersType, materialType } = source

  let errors = []
  errors = Object.entries(source).reduce((result, [key, val]) => {
    const log = () => result.push('invalid ' + key + ' value ' + val)

    val.trim()

    // const keyUpper = key.toUpperCase()
    const valUpper = val.toUpperCase()
    let my = 0
    if (typeof val === 'object') {
      result.push('invalid name ' + key)
    } else if (
      key === 'holdownDesign' ||
      key === 'foundationDesign' ||
      key === 'installOverSheathing' ||
      key === 'raisedFloorData'
    ) {
      if (valUpper !== 'YES' && valUpper !== 'Y' && valUpper !== 'NO' && valUpper !== 'N') {
        log()
      } else {
        valUpper === 'YES' || valUpper === 'Y' ? (source[key] = true) : (source[key] = false)
      }
    } else if (key === 'anchorageDesign') {
      if (valUpper !== 'YES' && valUpper !== 'Y' && valUpper !== 'NO' && valUpper !== 'N') {
        log()
      } else {
        valUpper === 'YES' || valUpper === 'Y'
          ? (source['foundationDesign'] = true)
          : (source['foundationDesign'] = false)
      }
    } else if (key === 'holdownSubType') {
      val !== '0' && val !== '1' ? log() : null
    } else if (key === 'anchorageOption') {
      if (!materialType) {
        log()
      } else {
        checkOptions(val, anchorageOptions[materialType]) ? null : log()
      }
    } else if (key === 'upliftLoadASD') {
      val < 0 || val > 20000 ? log() : null
    } else if (key === 'wallThickness') {
      checkOptions(val, wallThicknessData) ? null : log()
    } else if (key === 'holdownFastenersType') {
      checkOptions(val, holdownFastenersTypeData) ? null : log()
    } else if (key === 'lumberSpecies') {
      checkOptions(val, lumberSpeciesData) ? null : log()
    } else if (key === 'materialType') {
      checkOptions(val, materialTypeData.holdownSubType.embeddedAnchorOptions) ? null : log()
    } else if (key === 'loadType') {
      checkOptions(val, loadTypeData) ? null : log()
    } else {
      // result.push('invalid name ' + key )
    }

    return result
  }, [])

  delete source.anchorageDesign

  if (source.holdownDesign === true && !source.foundationDesign) {
    errors = validate1A(source, errors, true)
  } else if (
    source.holdownDesign === true &&
    source.foundationDesign === true &&
    source.holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP
  ) {
    errors = validate1B(source, errors)
  } else if (!source.holdownDesign && source.foundationDesign === true) {
    errors = validate1CDE(source, errors, false)
  } else if (source.holdownDesign === true && source.foundationDesign === true) {
    errors = validate1ACDE(source, errors)
  } else {
    errors.push('invalid URL')
  }

  // console.log(errors)

  return errors
}

export const updateInitialStateWithParams = (params) => {
  const myInitial = Object.assign({}, initValue, params)

  return myInitial
}

export const getPaneClassName = (width) => {
  if (width < 540) return '-xs'
  else if (width < 768) return '-sm'
  else if (width < 992) return '-md'
  else if (width < 1600) return '-lg'
  else if (width < 2500) return '-xl'
  else return '-xxl'
}

export const updatePaneClassName = (pane, length) => {
  const className = getPaneClassName(length) // length could be either width or height
  pane.classList.remove('-xs', '-sm', '-md', '-lg', '-xl', '-xxl')
  pane.classList.add(className)
  if (length < 250) {
    pane.classList.add('-minimized')
  } else {
    pane.classList.remove('-minimized')
  }
}

export const getHoldownLumberSpeciesOptions = (country, holdownPostBaseSubType) => {
  if (country === COUNTRY.CANADA) {
    if (holdownPostBaseSubType === HOLDOWN_TYPE.POST_INSTALLED) {
      return lumberSpeciesData.canada.holdown.postInstalledOptions
    }
    if (holdownPostBaseSubType === HOLDOWN_TYPE.CAST_IN_PLACE) {
      return lumberSpeciesData.canada.holdown.castInPlaceOptions
    }
  }
  return lumberSpeciesData.usa.holdownOptions
}

export const getLumberSpeciesDefaultValue = (country) => {
  if (country === COUNTRY.CANADA) {
    return POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].DOUGLAS_FIR_LARCH
  }
  return LUMBER_SPECIES.DOUGLAS_FIR_LARCH
}

export const getPostWidthOptions = (holdownDesign, lumberSpecies) => {
  const speciesOptionsKeys = {
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].DOUGLAS_FIR_LARCH]: 'douglasFirLarchOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].SPRUCE_PINE_FIR]: 'sprucePineFirOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].DOUGLAS_FIR_LARCH_ROUGH]:
      'douglasFirLarchRoughOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].SPRUCE_PINE_FIR_ROUGH]: 'sprucePineFirRoughOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].LSL]: 'lslOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].LVL]: 'lvlOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].PSL]: 'pslOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].GLULAM_DF]: 'glulamDfOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].GLULAM_SPF]: 'glulamSpfOptions',
  }

  const baseType = holdownDesign ? 'holdown' : 'postbase'
  const optionKey = speciesOptionsKeys[lumberSpecies]

  return optionKey ? postWidthData[baseType].lumberSpecies[optionKey] : []
}

export const getPostThicknessOptions = (holdownDesign, lumberSpecies) => {
  const speciesOptionsKeys = {
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].DOUGLAS_FIR_LARCH]: 'douglasFirLarchOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].SPRUCE_PINE_FIR]: 'sprucePineFirOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].DOUGLAS_FIR_LARCH_ROUGH]:
      'douglasFirLarchRoughOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].SPRUCE_PINE_FIR_ROUGH]: 'sprucePineFirRoughOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].LSL]: 'lslOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].LVL]: 'lvlOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].PSL]: 'pslOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].GLULAM_DF]: 'glulamDfOptions',
    [POST_BASE_LUMBER_SPECIES[COUNTRY.CANADA].GLULAM_SPF]: 'glulamSpfOptions',
  }

  const baseType = holdownDesign ? 'holdown' : 'postbase'
  const optionKey = speciesOptionsKeys[lumberSpecies]

  return optionKey ? postThicknessData[baseType].lumberSpecies[optionKey] : []
}

export const getDefaultOptValue = (opts) => {
  const defaultOpt = opts.find((item) => item.default)
  return defaultOpt ? defaultOpt.value : ''
}

export const getTextFromOptions = (options, value) => find(options, { value })?.text ?? 'N/A'

export const migrateJSONUpload = (inputState) => {
  const valueMapping = {
    postBaseFastenersType: {
      0: [POST_BASE_FASTENERS_TYPE.ALL],
      1: [POST_BASE_FASTENERS_TYPE.NAILS],
      2: [POST_BASE_FASTENERS_TYPE.SCREWS],
      3: [POST_BASE_FASTENERS_TYPE.BOLTS],
      4: [POST_BASE_FASTENERS_TYPE.DOWELS],
    },
    holdownFastenersType: {
      0: [HOLDOWN_FASTENERS.ALL],
      1: [HOLDOWN_FASTENERS.SCREWS],
      2: [HOLDOWN_FASTENERS.BOLTS],
      3: [HOLDOWN_FASTENERS.NAILS],
    },
    corrosionResistance: {
      All: [CORROSION_RESISTANCE_VALUE.ALL],
    },
  }

  const defaultValues = {
    settings: SETTINGS_INITIAL_STATE,
    holdownFastenersType: [HOLDOWN_FASTENERS.ALL],
    postBaseFastenersType: [POST_BASE_FASTENERS_TYPE.ALL],
    corrosionResistance: inputState.corrosionResistance,
  }

  const mappedValues = Object.keys(valueMapping).reduce((acc, key) => {
    acc[key] = valueMapping[key][inputState[key]] || defaultValues[key]
    return acc
  }, {})

  const mergedState = merge({}, defaultValues, inputState, mappedValues)

  if (mergedState.corrosionResistance.length === 3) {
    mergedState.corrosionResistance = [CORROSION_RESISTANCE_VALUE.ALL]
  }

  if (mergedState.geometryType === GEOMETRY_TYPE.TURNED_DOWN_SLAB) {
    mergedState.geometrySubType = GEOMETRY_SUBTYPE.MIDWALL_ANCHOR
  }

  return mergedState
}
